// The Shipping hub's order board (Phase 1, 2026-10-27): jobq-api GET /shipping/so-board?out_days=14
// (shipping_board.py). One row per live packing plan; each order lists the lanes it belongs to:
//   pack       a pallet whose printed status label reads READY TO SEAL (pack_state 'ready_to_seal'), or a holding
//              pallet whose pieces reached Packing (pack_state 'ready_to_pack')
//   not_ready  not in pack, but pallets with pieces on them that can't be packed yet: each pallet's pack_state says
//              why (at_vendor / to_vendor / at_station / in_progress / unknown), with its label {state, station,
//              dest, more} and outsourced {vendor, opo_number, sent_at}. Shown under Pack, collapsed.
//   ready      any sealed (Ready) pallet: ship it, or stage it for pickup
//   pickup     a pickup order with a Staged pallet: the customer can take it
//   out        a delivery ("We Deliver") order with a pallet shipped in the last out_days and no delivery record yet
// A holding pallet (wip_task_id set, still Building) is never shippable. (2026-10-27: Pack had listed every pallet
// with pieces, including pallets at the coater, to coating and to the brake.)
//
// Pure helpers (lanes, search, words, the plan the pickup action reads) + the two reads (never throw).
import { parseScanCode } from '../../utils/scanCodes';
import { shopToday } from '../../utils/shopDate';
import { detailToString } from '../../utils/apiError';

export const BOARD_LANES = Object.freeze([
  { id: 'pack', label: 'Pack' },
  { id: 'ready', label: 'Ready to ship' },
  { id: 'pickup', label: 'Ready for pickup' },
  { id: 'out', label: 'Out for delivery' },
]);

export const OUT_DAYS = 14;

/** A board body, shaped: {counts, orders}. Tolerates null, [], missing keys. */
export function normalizeBoard(body) {
  const obj = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  const orders = Array.isArray(obj.orders) ? obj.orders.filter((o) => o && typeof o === 'object') : [];
  const counts = obj.counts && typeof obj.counts === 'object' ? obj.counts : {};
  const laneCount = (id) => (Number.isFinite(Number(counts[id])) ? Number(counts[id])
    : orders.filter((o) => (o.lanes || []).includes(id)).length);
  return {
    counts: {
      pack: laneCount('pack'), ready: laneCount('ready'), pickup: laneCount('pickup'), out: laneCount('out'),
      not_ready: laneCount('not_ready'),
    },
    orders: orders.map((o) => ({
      ...o,
      lanes: Array.isArray(o.lanes) ? o.lanes : [],
      counts: o.counts && typeof o.counts === 'object' ? o.counts : {},
      pallets: Array.isArray(o.pallets) ? o.pallets : [],
    })),
  };
}

async function getJson(url) {
  try {
    const res = await fetch(url);
    let body = null;
    try { body = await res.json(); } catch (_) { body = null; }
    if (!res.ok) {
      const d = body && typeof body === 'object' ? body.detail : null;
      return { ok: false, status: res.status, message: detailToString(d) || `HTTP ${res.status}` };
    }
    return { ok: true, body };
  } catch (_) {
    return { ok: false, status: 0, message: 'Could not reach JobQ.' };
  }
}

/** GET /shipping/so-board. {ok, board} | {ok:false, message}. */
export async function loadSoBoard(apiBase, { outDays = OUT_DAYS } = {}) {
  const r = await getJson(`${apiBase}/shipping/so-board?out_days=${encodeURIComponent(outDays)}`);
  if (!r.ok) return { ok: false, message: r.message };
  return { ok: true, board: normalizeBoard(r.body) };
}

const byDue = (a, b) => {
  const da = a.promised_date || '9999-12-31';
  const db = b.promised_date || '9999-12-31';
  if (da !== db) return da < db ? -1 : 1;
  return String(a.order_number || '').localeCompare(String(b.order_number || ''), undefined, { numeric: true });
};

/** The orders in one lane, soonest promised date first (no date last). */
export function ordersInLane(board, lane) {
  return ((board && board.orders) || []).filter((o) => o.lanes.includes(lane)).sort(byDue);
}

/** The sales orders that ship from a live packing plan (the "Ship by job" rows for them are closed). */
export function livePlanOrderIds(board) {
  return new Set(((board && board.orders) || []).map((o) => Number(o.sales_order_id)).filter(Number.isFinite));
}

const norm = (v) => String(v == null ? '' : v).toLowerCase();

/** Whether an order matches a typed search: SO number (with or without "SO-"), customer, short code, PO,
 *  a pallet's PLT number or its beacon. */
export function orderMatches(order, query) {
  const q = norm(query).trim();
  if (!q) return true;
  const hay = [
    order.order_number, order.customer_name, order.customer_short_code, order.customer_po,
    ...(order.pallets || []).flatMap((p) => [p.short_id, p.beacon]),
  ].map(norm);
  if (hay.some((h) => h.includes(q))) return true;
  const digits = q.replace(/^so-?/, '');
  return /^\d+$/.test(digits) && norm(order.order_number).replace(/^so-?/, '') === digits;
}

export function filterOrders(orders, query) {
  return (orders || []).filter((o) => orderMatches(o, query));
}

/** Where a search box entry leads: a JobQ code (scanned or typed) on the board opens that order's package
 *  guide on that pallet; a code not on the board needs GET /scan/resolve; anything else filters the lists.
 *  {open: {so, pallet}} | {resolve: parsed} | {filter: text}. */
export function searchTarget(board, text) {
  const parsed = parseScanCode(text);
  if (!parsed || !['plt', 'bcn'].includes(parsed.kind)) return { filter: String(text || '') };
  const orders = (board && board.orders) || [];
  for (const o of orders) {
    for (const p of o.pallets) {
      if (parsed.kind === 'plt' && Number(p.pallet_id) === parsed.key) {
        return { open: { so: o.sales_order_id, pallet: p.pallet_id } };
      }
      if (parsed.kind === 'bcn' && p.beacon && beaconNumber(p.beacon) === parsed.key) {
        return { open: { so: o.sales_order_id, pallet: p.pallet_id } };
      }
    }
  }
  return { resolve: parsed };
}

/** BCN-012 -> 12. */
export function beaconNumber(beacon) {
  const m = /(\d+)\s*$/.exec(String(beacon || ''));
  return m ? parseInt(m[1], 10) : null;
}

/** A code that isn't on the board: GET /scan/resolve. {open: {so, pallet}} | {message}. */
export async function resolveSearchCode(apiBase, parsed) {
  const r = await getJson(`${apiBase}/scan/resolve?code=${encodeURIComponent(parsed.code)}`);
  if (!r.ok) return { message: r.message };
  const b = r.body || {};
  if (b.sales_order_id) return { open: { so: b.sales_order_id, pallet: b.pallet_id || null } };
  if (parsed.kind === 'bcn') {
    return { message: `${b.beacon_id || 'That beacon'} is not on a sales-order pallet on this board.` };
  }
  return { message: `${b.short_id || 'That pallet'} is on no sales order.` };
}

/** A Building plan pallet with pieces whose label says it can't be packed yet (its reason: notReadyText). */
const isNotReady = (p) => p.status === 'Building' && !p.holding && Number(p.pieces) > 0
  && p.pack_state !== 'ready_to_seal';

/** The pallets an order's lane is about (the package guide opens on the first): Pack = the pallets ready to seal,
 *  then the holding pallets ready to pack; Not ready = the pallets that can't be packed yet. */
export function lanePallets(order, lane) {
  const ps = (order && order.pallets) || [];
  if (lane === 'pack') {
    return [
      ...ps.filter((p) => p.status === 'Building' && !p.holding && p.pack_state === 'ready_to_seal'),
      ...ps.filter((p) => p.holding && p.status === 'Building' && p.pack_state === 'ready_to_pack'),
    ];
  }
  if (lane === 'not_ready') return ps.filter(isNotReady);
  if (lane === 'ready') return ps.filter((p) => p.status === 'Ready');
  if (lane === 'pickup') return ps.filter((p) => p.status === 'Staged');
  if (lane === 'out') return ps.filter((p) => p.status === 'Shipped' && !p.delivered_at);
  return [];
}

export const palletName = (p) => (p && (p.short_id || (p.pallet_num != null ? `P${p.pallet_num}` : `P#${p.pallet_id}`))) || '';

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Customer pickup" / "We deliver" / "Carrier". */
export function fulfillmentText(t) {
  if (t === 'pickup') return 'Customer pickup';
  if (t === 'delivery') return 'We deliver';
  return 'Carrier';
}

/** Due date words + tone: {text, late}. */
export function dueText(promised, today = shopToday()) {
  if (!promised) return { text: 'No promised date', late: false };
  const d = String(promised).slice(0, 10);
  const [y, m, day] = d.split('-').map(Number);
  const label = new Date(y, (m || 1) - 1, day || 1).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (d < today) return { text: `Was due ${label}`, late: true };
  if (d === today) return { text: 'Due today', late: false };
  return { text: `Due ${label}`, late: false };
}

const pcs = (n) => `${n} ${n === 1 ? 'pc' : 'pcs'}`;

/** Why a pallet can't be packed yet, in plain words: {text, outsourcing} (outsourcing: send it from the
 *  Outsourcing tab). From the pallet's pack_state / label / outsourced (jobq-api so-board). */
export function notReadyText(p) {
  const label = (p && p.label) || {};
  const out = (p && p.outsourced) || null;
  const state = p && p.pack_state;
  if (state === 'at_vendor' || out) {
    const vendor = (out && out.vendor) || 'the vendor';
    return { text: `At ${vendor}${out && out.opo_number ? ` (${out.opo_number})` : ''}`, outsourcing: false };
  }
  if (state === 'to_vendor') {
    const station = String(label.station || 'outside processing').trim();
    return { text: `To ${station.toLowerCase()}: send it from Outsourcing`, outsourcing: true };
  }
  if (state === 'at_station') {
    const more = Array.isArray(label.more) ? label.more.length : 0;
    return { text: `At ${label.station || 'the next station'}${more ? ` +${more} more` : ''}`, outsourcing: false };
  }
  if (state === 'in_progress') return { text: 'Still being packed', outsourcing: false };
  return { text: 'Status unknown', outsourcing: false };
}

/** The holding pallets still on their way to a station: "1 holding pallet still going to Brake". */
function holdingLine(order) {
  const going = order.pallets.filter((p) => p.holding && p.status === 'Building' && p.wip_station !== 'Packing');
  if (!going.length) return null;
  const stations = [...new Set(going.map((p) => p.wip_station).filter(Boolean))];
  return `${plural(going.length, 'holding pallet')} still going to ${stations.join(', ') || 'its next station'}`;
}

/** The lines under an order in a lane, in plain words. */
export function laneLines(order, lane) {
  const c = order.counts || {};
  const lines = [];
  if (lane === 'pack') {
    const ps = lanePallets(order, 'pack');
    const seal = ps.filter((p) => !p.holding).length;
    const hold = ps.filter((p) => p.holding).length;
    if (seal) lines.push(`${plural(seal, 'pallet')} ready to seal`);
    if (hold) lines.push(hold === 1 ? 'Holding pallet ready to pack' : `${hold} holding pallets ready to pack`);
    const n = c.pieces_ready_to_pack != null ? Number(c.pieces_ready_to_pack) || 0
      : ps.reduce((t, p) => t + Number(p.pieces || 0), 0);
    if (n) lines.push(`${pcs(n)} on the ${ps.length === 1 ? 'pallet' : 'pallets'}`);
    const notReady = lanePallets(order, 'not_ready').length;
    if (notReady) lines.push(`${plural(notReady, 'other pallet')} not ready yet`);
    const h = holdingLine(order);
    if (h) lines.push(h);
  } else if (lane === 'not_ready') {
    const ps = lanePallets(order, 'not_ready');
    const n = ps.reduce((t, p) => t + Number(p.pieces || 0), 0);
    if (n) lines.push(`${pcs(n)} on the ${ps.length === 1 ? 'pallet' : 'pallets'}`);
    // the pallets and their reasons are drawn by notReadyText (one line each, with the Outsourcing link)
    return lines;
  } else if (lane === 'ready') {
    const n = lanePallets(order, 'ready').length || Number(c.ready || 0);
    const verb = order.fulfillment_type === 'pickup' ? 'mark ready for pickup' : order.fulfillment_type === 'delivery' ? 'load for delivery' : 'ship';
    lines.push(`${plural(n, 'sealed pallet')} ready to ${verb}`);
  } else if (lane === 'pickup') {
    const ps = lanePallets(order, 'pickup');
    lines.push(`${plural(ps.length || Number(c.staged || 0), 'pallet')} waiting for the customer`);
  } else if (lane === 'out') {
    const ps = lanePallets(order, 'out');
    const last = ps.map((p) => p.shipped_at).filter(Boolean).sort().pop();
    const when = last ? new Date(last).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
    lines.push(`${plural(ps.length, 'pallet')} out on our truck${when ? ` since ${when}` : ''}`);
  }
  const names = lanePallets(order, lane).map(palletName).filter(Boolean);
  if (names.length) lines.push(names.slice(0, 6).join(', ') + (names.length > 6 ? ` +${names.length - 6} more` : ''));
  return lines;
}

/** The one big button for an order in a lane: {kind: 'open' | 'pickup' | 'load', label}. `signing` = the
 *  shipping_signing switches (./signingFlags): with `delivery` on, a delivery order's Ready button is Load truck. */
export function primaryAction(order, lane, signing = null) {
  if (lane === 'pickup') return { kind: 'pickup', label: 'Customer is here: start pickup' };
  if (lane === 'ready') {
    if (order.fulfillment_type === 'pickup') return { kind: 'open', label: 'Mark ready for pickup' };
    if (order.fulfillment_type === 'delivery' && signing && signing.delivery === true) return { kind: 'load', label: 'Load truck' };
    if (order.fulfillment_type === 'delivery') return { kind: 'open', label: 'Load for delivery' };
    return { kind: 'open', label: 'Ship' };
  }
  if (lane === 'out' || lane === 'not_ready') return { kind: 'open', label: 'Open' };
  return { kind: 'open', label: 'Pack' };
}

/** The little plan palletActions.confirmPickupAll reads, built from a board row. */
export function pickupPlan(order) {
  return {
    plan_id: order.plan_id,
    order_number: order.order_number,
    fulfillment_type: order.fulfillment_type,
    pallets: (order.pallets || []).map((p) => ({
      pallet_id: p.pallet_id, short_id: p.short_id || null, pallet_num: p.pallet_num ?? null, status: p.status,
    })),
  };
}
